import { useState } from 'react'
import { X, AlertTriangle, Clock, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useAgencyProfile } from '@/hooks/useAgencyProfile'
import { toDate } from '@/lib/firebase/firestore'
import { createCheckoutSession } from '@/services/billing'
import { BILLING_ENABLED } from '@/lib/featureFlags'

interface TrialBannerProps {
  onDismissChange?: (dismissed: boolean) => void
}

export default function TrialBanner({ onDismissChange }: TrialBannerProps = {}) {
  const { user } = useAuth()
  const { agency } = useAgencyProfile(user?.agencyId)
  const [dismissed, setDismissed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  if (!BILLING_ENABLED) return null

  // Only show to admins and supervisors
  if (!user || !['admin', 'supervisor'].includes(user.role)) return null
  if (dismissed) return null
  if (!agency?.subscription) return null

  const { subscription } = agency
  if (subscription.status !== 'trial') return null

  const trialEnd = toDate(subscription.trialEndsAt)
  if (!trialEnd) return null

  const now = new Date()
  const diffMs = trialEnd.getTime() - now.getTime()
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  // Don't show banner if more than 14 days left
  if (daysLeft > 14) return null

  // Already expired — the SubscriptionGuard handles this
  if (daysLeft <= 0) return null

  const isUrgent = daysLeft <= 3
  const isWarning = daysLeft <= 7

  return (
    <div
      className={`sticky top-0 z-[60] relative flex h-12 items-center justify-center gap-2 px-4 text-sm font-medium ${
        isUrgent
          ? 'bg-error-600 text-white'
          : isWarning
          ? 'bg-warning-500 text-white'
          : 'bg-primary-600 text-white'
      }`}
    >
      {isUrgent ? (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      ) : (
        <Clock className="h-4 w-4 shrink-0" />
      )}
      <span>
        {daysLeft === 1
          ? 'Your free trial ends tomorrow!'
          : `Your free trial ends in ${daysLeft} days.`}
        {' '}
        <button
          onClick={async () => {
            setIsLoading(true)
            try {
              const data = await createCheckoutSession(window.location.origin)
              if (data.url) window.location.assign(data.url)
            } catch {
              window.location.assign('/settings?tab=billing')
            } finally {
              setIsLoading(false)
            }
          }}
          disabled={isLoading}
          className="underline font-semibold hover:opacity-90 disabled:opacity-70"
        >
          {isLoading ? <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> : null}
          Upgrade now
        </button>
      </span>
      <button
        onClick={() => { setDismissed(true); onDismissChange?.(true) }}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:opacity-80"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
